/**
 * Printable "Cierre de caja" for one day: totals by method and by recorder, the payment list
 * and signature lines. One or more letter pages (rows are paginated by hand so each printed page
 * is a fixed 8.5in x 11in sheet).
 */

import { formatPrice } from '@/models/event';
import { formatReceiptNumber } from '@/features/receipts/types';
import { PAYMENT_METHOD_LABELS, type EventPayment, type EventPaymentMethod } from '@/Services/eventPayments.service';
import { paginateRows, summarizePayments } from '../utils/dailyClose';

interface DailyCloseSheetProps {
  eventName: string;
  /** 'YYYY-MM-DD' */
  date: string;
  /** Payments of that day (recorded and reversed) */
  payments: EventPayment[];
}

const payerName = (p: EventPayment) => p.participant?.minister?.full_name ?? p.participant?.guest?.full_name ?? '—';

const formatDay = (date: string) =>
  new Date(`${date}T12:00:00`).toLocaleDateString('es-BO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

function Page({ children, pageNo, pages }: { children: React.ReactNode; pageNo: number; pages: number }) {
  return (
    <div
      data-print-page
      className="relative flex flex-col overflow-hidden bg-white text-black shadow-lg"
      style={{ width: '8.5in', height: '10.96in', padding: '0.6in', fontFamily: 'Arial, Helvetica, sans-serif' }}
    >
      <div className="flex-1">{children}</div>
      <p className="text-right text-[10px] text-gray-500">
        Página {pageNo} de {pages}
      </p>
    </div>
  );
}

export function DailyCloseSheet({ eventName, date, payments }: DailyCloseSheetProps) {
  const recorded = payments
    .filter((p) => p.status === 'RECORDED')
    .sort((a, b) => a.paid_at.localeCompare(b.paid_at));
  const reversed = payments.filter((p) => p.status === 'REVERSED');
  const summary = summarizePayments(payments);
  const { pages, signatureOnOwnPage } = paginateRows(recorded);
  const totalPages = pages.length + (signatureOnOwnPage ? 1 : 0);
  const reversedTotal = reversed.reduce((sum, p) => sum + Number(p.amount), 0);

  const methods = [...summary.byMethod.entries()];

  const signatures = (
    <div className="mt-8 grid grid-cols-2 gap-16 text-center text-xs">
      {['Entregado por', 'Recibido por'].map((label) => (
        <div key={label}>
          <div className="h-14 border-b border-black" />
          <p className="mt-1 font-semibold uppercase tracking-wide text-gray-600">{label}</p>
          <p className="text-[10px] text-gray-500">Nombre, C.I. y firma</p>
        </div>
      ))}
    </div>
  );

  return (
    <>
      {pages.map((rows, pageIndex) => {
        const isFirst = pageIndex === 0;
        const isLast = pageIndex === pages.length - 1;
        return (
          <Page key={pageIndex} pageNo={pageIndex + 1} pages={totalPages}>
            {isFirst && (
              <>
                <div className="flex items-center gap-3">
                  <img src="/receipt/shield.png" alt="" style={{ width: '0.8in', height: '0.8in' }} />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide">Las Asambleas de Dios de Bolivia</p>
                    <h1 className="text-xl font-bold uppercase tracking-wide">Cierre de caja</h1>
                    <p className="text-sm font-semibold">{eventName}</p>
                    <p className="text-xs capitalize text-gray-600">{formatDay(date)}</p>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                  <div className="rounded border border-gray-400 p-2">
                    <p className="text-[10px] uppercase tracking-wide text-gray-500">Total del día</p>
                    <p className="text-lg font-bold">{formatPrice(summary.total)}</p>
                    <p className="text-[10px] text-gray-500">{summary.count} pago(s)</p>
                  </div>
                  <div className="col-span-2 rounded border border-gray-400 p-2">
                    <p className="text-[10px] uppercase tracking-wide text-gray-500">Por método</p>
                    {methods.length === 0 ? (
                      <p className="text-xs text-gray-500">Sin pagos</p>
                    ) : (
                      <div className="mt-1 grid grid-cols-2 gap-x-4 text-xs">
                        {methods.map(([method, m]) => (
                          <div key={method} className="flex justify-between">
                            <span>{method === 'none' ? 'Sin método' : PAYMENT_METHOD_LABELS[method as EventPaymentMethod]} ({m.count})</span>
                            <span className="font-semibold">{formatPrice(m.total)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {summary.byRecorder.length > 0 && (
                  <p className="mt-2 text-xs text-gray-700">
                    <span className="font-semibold">Registrado por: </span>
                    {summary.byRecorder.map((r) => `${r.name} ${formatPrice(r.total)} (${r.count})`).join(' · ')}
                  </p>
                )}
                {reversed.length > 0 && (
                  <p className="mt-1 text-xs text-rose-700">
                    Pagos revertidos este día (no incluidos): {reversed.length} por {formatPrice(reversedTotal)}
                  </p>
                )}
              </>
            )}

            <table className="mt-4 w-full border-collapse text-xs">
              <thead>
                <tr className="border-b-2 border-black text-left">
                  <th className="py-1 pr-2 font-semibold">Hora</th>
                  <th className="py-1 pr-2 font-semibold">Participante</th>
                  <th className="py-1 pr-2 font-semibold">Método</th>
                  <th className="py-1 pr-2 font-semibold">Recibo</th>
                  <th className="py-1 text-right font-semibold">Monto</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className="border-b border-gray-300">
                    <td className="py-1 pr-2">
                      {new Date(p.paid_at).toLocaleTimeString('es-BO', { hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="py-1 pr-2">{payerName(p)}</td>
                    <td className="py-1 pr-2">{p.method ? PAYMENT_METHOD_LABELS[p.method] : '—'}</td>
                    <td className="py-1 pr-2">{p.receipt ? `N° ${formatReceiptNumber(p.receipt.number)}` : 'Sin recibo'}</td>
                    <td className="py-1 text-right font-medium">{formatPrice(Number(p.amount))}</td>
                  </tr>
                ))}
                {recorded.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-6 text-center text-gray-500">
                      No hay pagos registrados este día.
                    </td>
                  </tr>
                )}
              </tbody>
              {isLast && recorded.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={4} className="pt-2 text-right font-semibold">
                      Total
                    </td>
                    <td className="pt-2 text-right text-sm font-bold">{formatPrice(summary.total)}</td>
                  </tr>
                </tfoot>
              )}
            </table>

            {isLast && !signatureOnOwnPage && signatures}
          </Page>
        );
      })}
      {signatureOnOwnPage && (
        <Page pageNo={totalPages} pages={totalPages}>
          <p className="text-sm font-semibold">
            Cierre de caja — {eventName} — {formatDay(date)}
          </p>
          <p className="mt-1 text-sm">Total: {formatPrice(summary.total)}</p>
          {signatures}
        </Page>
      )}
    </>
  );
}
